import { type App, type Component } from 'vue'
import { DynamicInput } from './input/install'
import { DynamicSelect } from './select/install'
import { DynamicRadio } from './radio/install'
import { DynamicUploadSingle } from './upload/single/install'
import { DynamicUploadMultiple } from './upload/multiple/install'
import { DynamicUploadImage } from './upload/image/install'
import { DynamicP } from './p/install'
import type { ComponentItem } from './types'
import type { InputProps } from './input/types'
import type { SelectProps } from './select/types'
import type { RadioProps } from './radio/types'
import type { UploadSingleProps } from './upload/single/types'
import type { UploadMultipleProps } from './upload/multiple/types'
import type { UploadImageProps } from './upload/image/types'
import type { PProps } from './p/types'

/**
 * 子路径 `@sqt/common/dynamic` 公开出口。
 * 只给动态表单：`ComponentMap` / `asyncRegister`。
 * 跨页共用组件从 `@sqt/common/components` 取，不要再塞进这张物料表。
 */
export {
	DynamicInput,
	DynamicSelect,
	DynamicRadio,
	DynamicUploadSingle,
	DynamicUploadMultiple,
	DynamicUploadImage,
	DynamicP,
}
export {
	detectDynamicUploadFile,
	readDynamicUploadAccept,
	readDynamicUploadSizeBytes,
} from './upload/detect'

export type AsyncComponentLoader = () => Promise<{ default: Component }>
/** 动态表单物料 props 映射（只含可拖入画布的包装组件；Layout 壳从 `@sqt/common/layout` 取） */
export interface ComponentMap {
	DynamicInput: ComponentItem<InputProps>
	DynamicSelect: ComponentItem<SelectProps>
	DynamicRadio: ComponentItem<RadioProps>
	DynamicUploadSingle: ComponentItem<UploadSingleProps>
	DynamicUploadMultiple: ComponentItem<UploadMultipleProps>
	DynamicUploadImage: ComponentItem<UploadImageProps>
	DynamicP: ComponentItem<PProps>
}

export type ComponentName = keyof ComponentMap
export type ComponentMaps = {
	[K in ComponentName]: ComponentMap[K]
}

/**
 * 全局注册动态表单组件。
 * install 已经静态引了 SFC，再 `import()` 同一份拆不出新块，流水线会报 INEFFECTIVE_DYNAMIC_IMPORT。
 * 函数本身可当 Vue 插件：`app.use(asyncRegister)`，不要再包一层 default。
 */
export const asyncRegister = (app: App) => {
	const materials = {
		DynamicInput,
		DynamicSelect,
		DynamicRadio,
		DynamicUploadSingle,
		DynamicUploadMultiple,
		DynamicUploadImage,
		DynamicP,
	}
	for (const [name, component] of Object.entries(materials)) {
		app.component(name, component)
	}
}

export type * from './types'
export type * from './input/types'
export type * from './select/types'
export type * from './radio/types'
export type * from './upload/single/types'
export type * from './upload/multiple/types'
export type * from './upload/image/types'
export type * from './p/types'
